/** 公司普通股募集的只读披露；金额cent只在这里转元，估值和每股报价本来就是元。 */
import { dateOf, esc } from './sbchart.ts';
import { companyOriginText } from './sbcompany-origin.ts';
type Data = Record<string, any>;
export interface InvestmentLinks { firm(id: number, name: string): string }
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const money = (v: unknown) => finite(v) ? `${v.toFixed(2)} 元` : '未知';
const cents = (v: unknown) => finite(v) ? money(v / 100) : '未知';
const count = (v: unknown) => finite(v) ? String(v) : '未知';
const rate = (v: unknown) => finite(v) ? `${(v * 100).toFixed(2)}%` : '未知';
const row = (label: string, value: string) => `<div class="sb-row"><span>${esc(label)}</span><b>${value}</b></div>`;

export function investmentText(d: Data | null | undefined): string[] {
  if (!d) return ['此版本或公司未提供此项募集披露；不从现金、历史派息或估值反推可认购报价。'];
  const q = d.offer;
  const lines = ['募集与投资（当前真实报价与观察范围）',
    `采样日：${finite(d.day) ? dateOf(d.day) : '未知'} · 公司 #${count(d.fundId)}`,
    `当前募集判断：${d.fundraisingReason ?? '未提供原因'}`,
    `实体经营/持仓初筛候选：${count(d.candidateCount)} 家；实际报价仍须正净值及当前未募足缺口`,
    `存续期实际出资流入：${cents(d.raised)} · 存续期实际派息：${cents(d.distributed)}（派息可能包含返还本金，不等于经营利润）`,
    `股份出售及清算实收：${cents(d.saleAndLiquidationReceipts)}（总回款，未扣买入成本，不是净赚）`,
    `现金：${cents(d.cash)} · 经营准备金：${cents(d.cashReserve)} · 留款后可投自有现金：${cents(d.ownAvailable)}`,
    `已结观察窗口：${count(d.observedMonths)} 个月 / ${count(d.observedDays)} 天 · 已收分红：${cents(d.receivedDividends)} · 已付经营费用：${cents(d.operatingExpenses)}`,
    '费用是上述历史窗口的真实支付；0只表示该窗口未发生，不表示未来免费或无需经营成本。',
    `当前持仓中有经营月报依据：${count(d.verifiedPositions)} / ${count(d.positions)} 笔 · 按现持股分摊的估计年经营利润：${money(d.verifiedPortfolioAnnualProfit)}（未必派息到账）`,
    `公司自身净资产：${money(d.ownNetAssets)} · 一层持仓估值：${money(d.holdingsValue)} · 合计报价净值：${money(d.netValue)}`,
    `登记普通股：${count(d.totalShares)} 股 · 每股报价：${money(d.perShare)}`];
  lines.push(`常规列表/财报估值参考：${money(d.referenceCompanyValue)} · 常规公式每股：${money(d.referencePerShare)}（沿当日估值缓存，与此刻重建的募集报价分列，不等同成交或赎回）`);
  if (q) lines.push(`当前可投企业：${q.targetName ?? '名称未知'} · 公司 #${count(q.targetId)}`,
    `实体当前资本缺口：${cents(q.targetGap)} · 本次追加募集需求：${cents(q.fundingGap)} · 需先补的准备金：${cents(q.reserveShortfall)}`,
    `拟投企业报价：每股 ${money(q.targetPrice)} · 投资公司新股报价：每股 ${money(q.issuePrice)}`,
    `拟投企业已观察：${count(q.targetObservedMonths)} 个月 · 年化历史经营利润：${money(q.targetAnnualProfit)} · 最近最多12期实际派息：${cents(q.targetDividends)}`,
    `合并持仓、拟投订单和实际费用的估计年经营回报：${rate(q.expectedAnnualReturn)}（不是已实现收益率或保证回报）`, q.basis ?? '报价收益依据未提供');
  else lines.push('当前没有可投实体报价；不虚列目标、追加募集额或预期收益。');
  for (const key of ['valuationScope', 'observationScope', 'feeScope', 'exitScope', 'returnScope']) if (d[key]) lines.push(String(d[key]));
  lines.push('这是当前判断快照，不代替真实交易记录；认购还须符合每位投资人的实际余额、生活留款和回报要求。');
  return lines;
}

export function investmentHTML(d: Data | null | undefined, links: InvestmentLinks): string {
  if (!d) return `<p class="meta">${esc(investmentText(d)[0])}</p>`;
  const q = d.offer;
  const sections = investmentText(d);
  return `<section class="sb-sec"><h4>为什么现在可募 / 不募</h4>${row('当前判断', esc(d.fundraisingReason ?? '未提供原因'))}` +
    row('当前报价采样', finite(d.day) ? dateOf(d.day) : '未知') +
    (q ? row('真实可投企业', links.firm(q.targetId, q.targetName ?? `公司 #${q.targetId}`)) +
      row('实体还缺资本', cents(q.targetGap)) + row('追加募集需求', cents(q.fundingGap)) + row('需先补准备金', cents(q.reserveShortfall)) +
      row('估计年经营回报', `${rate(q.expectedAnnualReturn)} · 尚未实现，不保证到账`) : '<p class="meta">当前没有可投实体报价，不显示虚构目标或预期收益。</p>') +
    row('现金 / 经营准备金', `${cents(d.cash)} / ${cents(d.cashReserve)}`) + row('留款后可投自有现金', cents(d.ownAvailable)) + '</section>' +
    `<section class="sb-sec"><h4>真实收付与估值边界</h4>${sections.slice(3).map(line => `<p class="meta">${esc(line)}</p>`).join('')}</section>`;
}

/** 公司TXT只整理同一份query；所有原始交易与冷档仍完整附在后面。 */
export function investmentCompanyText(x: Data): string {
  const d = x.data ?? {};
  return [`${x.name ?? d.name ?? '公司'} · 公司档案`, `公司 #${count(x.id)} · 城市种子 ${count(x.seed)} · 导出 ${finite(x.day) ? dateOf(x.day) : '日期未知'}`,
    x.recordScope ?? '资料覆盖范围未知', x.archive?.note ?? '冷档覆盖范围未提供', '', ...companyOriginText(d.origin), '', ...investmentText(d.investmentDisclosure), '',
    '原始完整资料附录（保留实际交易、归档记录、字段和原始单位）', JSON.stringify(x, null, 2)].join('\n');
}
